import { Injectable } from '@angular/core';
import { FilePicker, PickFilesOptions, PickedFile } from '@capawesome/capacitor-file-picker';

export interface ChooserOptions {
  mimeTypes?: string;
  maxFileSize?: number;
}

export interface ChooserResult {
  data?: Blob;
  displayName: string;
  extension: string;
  mimeType: string;
  name: string;
  path?: string;
  size: number;
}

@Injectable({ providedIn: 'root' })
export class ChooserService {
  async getFile(options?: ChooserOptions): Promise<ChooserResult | undefined> {
    try {
      const result = await FilePicker.pickFiles(this.buildPickerOptions(options));
      const file = result.files?.[0];
      if (!file) {
        return undefined;
      }

      if (options?.maxFileSize && file.size > options.maxFileSize) {
        throw new Error('MAX_FILE_SIZE_EXCEEDED');
      }

      const extension = this.extractExtension(file.name);
      const data = this.resolveFileData(file);

      return {
        data,
        displayName: file.name,
        extension,
        mimeType: file.mimeType,
        name: file.name,
        path: file.path,
        size: file.size,
      };
    } catch (error: any) {
      if (this.isCancellation(error)) {
        return undefined;
      }
      throw error;
    }
  }

  private buildPickerOptions(options?: ChooserOptions): PickFilesOptions {
    const types = this.parseMimeTypes(options?.mimeTypes);
    return {
      limit: 1,
      readData: true,
      types: types?.length ? types : undefined,
    };
  }

  private parseMimeTypes(mimeTypes?: string): string[] | undefined {
    return mimeTypes
      ?.split(',')
      .map(type => type.trim())
      .filter(Boolean);
  }

  private extractExtension(fileName: string): string {
    const parts = fileName.split('.');
    return parts.length > 1 ? parts.pop() || '' : '';
  }

  private resolveFileData(file: PickedFile): Blob | undefined {
    if (file.blob) {
      return file.blob;
    }

    if (file.data) {
      return this.base64ToBlob(file.data, file.mimeType);
    }

    return undefined;
  }

  private base64ToBlob(base64Data: string, mimeType: string): Blob {
    const byteCharacters = atob(base64Data);
    const byteArray = new Uint8Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteArray[i] = byteCharacters.charCodeAt(i);
    }

    return new Blob([byteArray], { type: mimeType });
  }

  private isCancellation(error: any): boolean {
    const message = typeof error === 'string' ? error : error?.message;
    return typeof message === 'string' && message.toLowerCase().includes('canceled');
  }
}
